import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getRetentionGraphData } from "../services/videoAnalytics";

const VIDEO_DURATION_SECONDS = 11 * 60;
const CHART_WIDTH = 860;
const CHART_HEIGHT = 320;
const CHART_PADDING = { top: 18, right: 18, bottom: 40, left: 48 };

type RetentionPoint = {
  checkpoint: number;
  viewers: number;
  retentionRate: number;
  dropoffCount: number;
};

const demoData: RetentionPoint[] = [
  { checkpoint: 0, viewers: 120, retentionRate: 100, dropoffCount: 0 },
  { checkpoint: 10, viewers: 108, retentionRate: 90, dropoffCount: 12 },
  { checkpoint: 20, viewers: 96, retentionRate: 80, dropoffCount: 12 },
  { checkpoint: 30, viewers: 82, retentionRate: 68, dropoffCount: 14 },
  { checkpoint: 40, viewers: 77, retentionRate: 64, dropoffCount: 5 },
  { checkpoint: 50, viewers: 59, retentionRate: 49, dropoffCount: 18 },
  { checkpoint: 60, viewers: 48, retentionRate: 40, dropoffCount: 11 },
  { checkpoint: 70, viewers: 36, retentionRate: 30, dropoffCount: 12 },
  { checkpoint: 80, viewers: 28, retentionRate: 23, dropoffCount: 8 },
  { checkpoint: 90, viewers: 20, retentionRate: 17, dropoffCount: 8 },
  { checkpoint: 100, viewers: 14, retentionRate: 12, dropoffCount: 6 },
];

const getMaxViewers = (points: RetentionPoint[]) =>
  points.reduce((max, point) => Math.max(max, point.viewers), 0);

const getBiggestDropoff = (points: RetentionPoint[]) =>
  points.reduce((worst, point) =>
    point.dropoffCount > worst.dropoffCount ? point : worst
  );

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return `${mins}:${String(secs).padStart(2, "0")}`;
};

const getTimeFromCheckpoint = (checkpoint: number) =>
  Math.round((checkpoint / 100) * VIDEO_DURATION_SECONDS);

const getChartPoint = (point: RetentionPoint) => {
  const innerWidth = CHART_WIDTH - CHART_PADDING.left - CHART_PADDING.right;
  const innerHeight = CHART_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom;
  const x = CHART_PADDING.left + (point.checkpoint / 100) * innerWidth;
  const y =
    CHART_PADDING.top + ((100 - point.retentionRate) / 100) * innerHeight;

  return { x, y };
};

const buildLinePath = (points: RetentionPoint[]) => {
  if (points.length === 0) {
    return "";
  }

  return points
    .map((point, index) => {
      const { x, y } = getChartPoint(point);
      return `${index === 0 ? "M" : "L"}${x} ${y}`;
    })
    .join(" ");
};

const buildAreaPath = (points: RetentionPoint[]) => {
  if (points.length === 0) {
    return "";
  }

  const linePath = buildLinePath(points);
  const lastPoint = getChartPoint(points[points.length - 1]);
  const firstPoint = getChartPoint(points[0]);
  const bottomY = CHART_HEIGHT - CHART_PADDING.bottom;

  return `${linePath} L${lastPoint.x} ${bottomY} L${firstPoint.x} ${bottomY} Z`;
};

const SimpleAnalyticsDemo = () => {
  const [points, setPoints] = useState<RetentionPoint[]>(demoData);
  const [loading, setLoading] = useState(true);
  const [usingDemoData, setUsingDemoData] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const liveData = await getRetentionGraphData("jba-course");

        if (!mounted) {
          return;
        }

        if (liveData.length > 10) {
          setPoints(liveData);
          setUsingDemoData(false);
        }
      } catch {
        if (!mounted) {
          return;
        }

        setPoints(demoData);
        setUsingDemoData(true);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      mounted = false;
    };
  }, []);

  const maxViewers = useMemo(() => getMaxViewers(points) || 1, [points]);
  const biggestDropoff = useMemo(() => getBiggestDropoff(points), [points]);
  const started = points[0]?.viewers ?? 0;
  const completed = points[points.length - 1]?.viewers ?? 0;
  const averageRetention =
    points.length > 0
      ? Math.round(
          points.reduce((sum, point) => sum + point.retentionRate, 0) /
            points.length
        )
      : 0;
  const linePath = useMemo(() => buildLinePath(points), [points]);
  const areaPath = useMemo(() => buildAreaPath(points), [points]);
  const yAxisTicks = [100, 75, 50, 25, 0];
  const xAxisTicks = [0, 25, 50, 75, 100];

  return (
    <div className="min-h-screen bg-[#040406] px-4 py-10 text-white">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(124,58,237,0.18),_transparent_34%),radial-gradient(circle_at_80%_20%,_rgba(245,158,11,0.16),_transparent_24%),linear-gradient(180deg,#050507_0%,#09090f_45%,#040406_100%)]" />

      <div className="mx-auto max-w-6xl rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-[0_32px_90px_-52px_rgba(124,58,237,0.6)] backdrop-blur sm:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#f6b31a]">
              Simple Retention Demo
            </p>
            <h1 className="mt-3 text-3xl font-black leading-tight sm:text-4xl">
              Audience retention over the full video
            </h1>
            <p className="mt-3 max-w-3xl text-sm text-white/65 sm:text-base">
              This is a YouTube-style retention view. The horizontal axis is the video
              timeline in minutes and seconds. The vertical axis is the percentage of
              viewers still watching at that moment.
            </p>
          </div>

          <Link className="text-sm text-[#f6b31a] underline-offset-4 hover:underline" to="/jba">
            Back to JBA page
          </Link>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <div className="rounded-[1.4rem] border border-white/10 bg-black/20 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">
              Started
            </p>
            <p className="mt-2 text-3xl font-black text-white">{started}</p>
          </div>
          <div className="rounded-[1.4rem] border border-white/10 bg-black/20 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">
              Completed
            </p>
            <p className="mt-2 text-3xl font-black text-white">{completed}</p>
          </div>
          <div className="rounded-[1.4rem] border border-white/10 bg-black/20 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">
              Biggest Dropoff
            </p>
            <p className="mt-2 text-3xl font-black text-[#f6b31a]">
              {formatTime(getTimeFromCheckpoint(biggestDropoff?.checkpoint ?? 0))}
            </p>
            <p className="mt-1 text-sm text-white/55">
              {biggestDropoff?.dropoffCount ?? 0} viewers lost there
            </p>
          </div>
          <div className="rounded-[1.4rem] border border-white/10 bg-black/20 p-4 md:col-span-3 lg:hidden">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">
              Average Retention
            </p>
            <p className="mt-2 text-3xl font-black text-white">{averageRetention}%</p>
          </div>
        </div>

        <div className="mt-6 rounded-[1.6rem] border border-white/10 bg-black/25 p-5">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-black text-white">Retention Graph {usingDemoData ? "(Demo)" : ""}</h2>
              <p className="text-sm text-white/55">
                {usingDemoData
                  ? "Showing demo data until your Supabase table has rows."
                  : "Showing live data from Supabase."}
              </p>
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/35">
              {loading ? "Loading" : "Ready"}
            </div>
          </div>

          <div className="overflow-x-auto rounded-[1.4rem] border border-white/10 bg-[#07080c] p-3 sm:p-5">
            <div className="min-w-[760px]">
              <svg
                viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                className="h-[320px] w-full"
                role="img"
                aria-label="Video retention line chart"
              >
                <defs>
                  <linearGradient id="retention-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a855f7" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#a855f7" stopOpacity="0.02" />
                  </linearGradient>
                </defs>

                {yAxisTicks.map((tick) => {
                  const y = getChartPoint({
                    checkpoint: 0,
                    viewers: maxViewers,
                    retentionRate: tick,
                    dropoffCount: 0,
                  }).y;

                  return (
                    <g key={tick}>
                      <line
                        x1={CHART_PADDING.left}
                        y1={y}
                        x2={CHART_WIDTH - CHART_PADDING.right}
                        y2={y}
                        stroke="rgba(255,255,255,0.10)"
                        strokeDasharray="4 6"
                      />
                      <text
                        x={CHART_PADDING.left - 10}
                        y={y + 4}
                        textAnchor="end"
                        fill="rgba(255,255,255,0.48)"
                        fontSize="12"
                      >
                        {tick}%
                      </text>
                    </g>
                  );
                })}

                {xAxisTicks.map((tick) => {
                  const x = getChartPoint({
                    checkpoint: tick,
                    viewers: 0,
                    retentionRate: 0,
                    dropoffCount: 0,
                  }).x;

                  return (
                    <g key={tick}>
                      <line
                        x1={x}
                        y1={CHART_PADDING.top}
                        x2={x}
                        y2={CHART_HEIGHT - CHART_PADDING.bottom}
                        stroke="rgba(255,255,255,0.06)"
                      />
                      <text
                        x={x}
                        y={CHART_HEIGHT - 10}
                        textAnchor="middle"
                        fill="rgba(255,255,255,0.48)"
                        fontSize="12"
                      >
                        {formatTime(getTimeFromCheckpoint(tick))}
                      </text>
                    </g>
                  );
                })}

                <path d={areaPath} fill="url(#retention-fill)" />
                <path
                  d={linePath}
                  fill="none"
                  stroke="#f6b31a"
                  strokeWidth="4"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />

                {points.map((point) => {
                  const chartPoint = getChartPoint(point);

                  return (
                    <g key={point.checkpoint}>
                      <circle
                        cx={chartPoint.x}
                        cy={chartPoint.y}
                        r="5"
                        fill="#0b0911"
                        stroke="#f6b31a"
                        strokeWidth="2"
                      />
                      <title>
                        {`${formatTime(getTimeFromCheckpoint(point.checkpoint))} · ${point.retentionRate}% still watching · ${point.viewers} viewers`}
                      </title>
                    </g>
                  );
                })}

                <text
                  x={18}
                  y={18}
                  fill="rgba(255,255,255,0.65)"
                  fontSize="12"
                  transform={`rotate(-90 18 18)`}
                >
                  Viewers
                </text>
              </svg>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-3 text-xs text-white/60">
            <div className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-2">
              Video length: {formatTime(VIDEO_DURATION_SECONDS)}
            </div>
            <div className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-2">
              Average retention: {averageRetention}%
            </div>
            <div className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-2">
              Checkpoints every 10% of the video
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-[1.6rem] border border-white/10 bg-black/20 p-5">
            <h2 className="text-lg font-black text-white">How the rows look</h2>
            <pre className="mt-4 overflow-x-auto rounded-2xl bg-[#050507] p-4 text-xs text-white/75">{`video_id     session_id           checkpoint_percent
jba-course   jba-session-abc123   0
jba-course   jba-session-abc123   10
jba-course   jba-session-abc123   20
jba-course   jba-session-abc123   30
jba-course   jba-session-abc123   40
jba-course   jba-session-abc123   50`}</pre>
          </div>

          <div className="rounded-[1.6rem] border border-white/10 bg-black/20 p-5">
            <h2 className="text-lg font-black text-white">Why this is enough</h2>
            <ul className="mt-3 space-y-3 text-sm text-white/70">
              <li>Each checkpoint becomes one point on the retention curve.</li>
              <li>The x-axis is mapped from checkpoint percent to actual video time.</li>
              <li>The y-axis is retention rate, from 0% to 100% of viewers.</li>
              <li>You still only need one simple checkpoint table for this chart.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleAnalyticsDemo;